import "./style.scss";

import CheckOutlined from "@mui/icons-material/CheckOutlined";
import { TabContext, TabPanel } from "@mui/lab";
import { Drawer } from "@mui/material";
import { getCleanLanguageCode } from "components/Beneficiar/utils";
import ModalBox from "components/Modal";
import { displayMobileNavBtn } from "components/shared/BPMobileNavBtn";
import BPNavigationBarSettings from "components/shared/BPNavigationBarSettings";
import BPAccount from "components/shared/BPPanelSettings/BPAccount";
import BPPanelHeader from "components/shared/BPPanelSettings/BPPanelHeader";
import BPPaymentMethod from "components/shared/BPPanelSettings/BPPaymentMethod";
import BPProfile from "components/shared/BPPanelSettings/BPProfile";
import BPTaxProfile from "components/shared/BPPanelSettings/BPTaxProfile";
import EmptyState from "components/shared/EmptyState";
import StyledButton from "components/shared/StyledButton";
import { extractEntityCustomFields, formatFieldDataForPortal } from "containers/CustomFieldsContainer/helpers";
import { fileWeightValidator } from "helpers/importData-helpers";
import { isObject } from "helpers/utils";
import { isValidBIC, isValidIBAN } from "ibantools";
import { useBPCompanySettings } from "hooks/useBPCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import usePageTitle from "hooks/usePageTitle";
import { useIsPWA } from "hooks/usePWAMode";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    fetchBeneficiarPersonalInfo,
    fetchUpdateBeneficaryFromPortal,
    getCompagnies,
    resetBeneficiarPersonalInfo,
} from "store/beneficiaryStore";
import type { ICustomField, StoreCustomField } from "store/customFieldsStore";
import { fetchDocumentsBP } from "store/documentStore";
import { toggleSettingsTab } from "store/settingsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";
import { sendPicture } from "store/titleStore";
import type { IBeneficiariesCompanies } from "store/types";
import { defaultCountryFeatures } from "types/countryFeatures";

import type { TEditingBeneficiarProfile } from "./types";

type TProps = {
    page: "account" | "tax" | "payment" | "documents" | "profile";
};

const formatPayload = (payload: any, company: any) => {
    if (!payload) return null;
    return {
        account: {
            name: payload.name ?? "",
            surname: payload.surname ?? "",
            birthDate: payload?.birthDate ?? null,
            socialSecurityNumber: payload.socialSecurityNumber ?? "",
            email: payload.email ?? "",
            phoneNumber: payload.phoneNumber ?? "",
            city: payload.city ?? "",
            country: payload.country ?? "",
            street: payload.street ?? "",
            zipCode: payload.zipCode ?? "",
            avatarUrl: payload.avatarUrl ?? "",
            siretNumber: payload.siretNumber ?? "",
            entity: payload.entity ?? "none",
            lang: getCleanLanguageCode(payload.lang) ?? "fr",
        },
        tax: {
            precount: payload.precount ?? "",
            socialProfileType: payload.socialProfileType ?? "",
            vat: payload.vat ?? "",
        },
        payment: {
            acceptedPayments: payload.acceptedPayments ?? "",
            bankCheck: payload.bankCheck ?? "",
            iban: payload.iban ?? "",
            paypalEmail: payload.paypalEmail ?? "",
            ribOwnerName: payload.ribOwnerName ?? "",
            swift: payload.swift ?? "",
            bankingMetadata: Array.isArray(payload.bankingMetadata) ? null : payload.bankingMetadata ?? null,
        },
        customFields: payload.customFields ?? [],
    };
};

const BPSettings = ({ page }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    usePageTitle(t("Settings.Settings"));
    const { isMobile } = useIsMobile();
    const isPWAMode = useIsPWA();
    const { getBPCompanySettings } = useBPCompanySettings({
        portalType: "beneficiary",
    });
    const onOpenSettingsTab = useAppSelector((state) => state.settings.showSettingsTab);

    const { beneficiarPersonalInfo, beneficiarCompagnies, isInvited } = useSelector(
        (state: RootState) => state.beneficiary,
    );

    const companyId = routeId ? +routeId : beneficiarCompagnies[0]?.id ?? undefined;
    const company = useMemo(() => {
        return beneficiarCompagnies.find((company) => company.id === +companyId);
    }, [beneficiarCompagnies, companyId]);

    const allowChanges = getBPCompanySettings("uses_portal_social_profile", (company as IBeneficiariesCompanies)?.id);
    const usesAgreementsOnly = getBPCompanySettings("uses_agreements_only", (company as IBeneficiariesCompanies)?.id);

    const countryFeatures = company?.countryFeatures ?? defaultCountryFeatures;
    const hasTaxFeatures =
        countryFeatures.precount.options.length > 0 ||
        countryFeatures.vat.options.length > 0 ||
        countryFeatures.precountMode === "social_profile_type";

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editingProfile, setEditingProfile] = useState<TEditingBeneficiarProfile | null>(
        Object.keys(beneficiarPersonalInfo ?? {}).length
            ? (formatPayload(beneficiarPersonalInfo, company) as TEditingBeneficiarProfile)
            : null,
    );

    const [blockModal, setBlockModal] = useState<boolean>(false);
    const blocker = useBlocker(isEditing);
    const [hasFieldErrors, setHasFieldErrors] = useState(false);
    const [recalculateErrors, setRecalculateErrors] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<string[]>([]); // array of field name / id (string) for custom fields
    const [ibanBicErrors, setIbanBicErrors] = useState<{ iban?: string; swift?: string }>({});

    /**
     * Array that contains custom fields created by company and for which the related resource is "beneficiary"
     */
    const companyBenefCustomFields: ICustomField[] | undefined = useMemo(() => {
        const companyCustomFields = company?.customFields;
        return extractEntityCustomFields(companyCustomFields, beneficiarPersonalInfo?.entity);
    }, [company, beneficiarPersonalInfo]);

    /**
     * Array that contains all data from benef custom fields: those saved on profile and those that aren't (to which we set an empty string as value).
     */
    const fullCustomFieldsData: StoreCustomField[] = useMemo(() => {
        const newCustomFieldsData: any = [];
        companyBenefCustomFields?.forEach((field) => {
            const originalData = editingProfile?.customFields?.find((item) => item.customFieldTypeId === field.id);
            // If the field already has data saved within it, I simply push it.
            if (originalData) {
                newCustomFieldsData.push(originalData);
            } else {
                // If the field doesn't exist yet on Benef, I add it with empty (string) value so it still appears.
                const compiledData = {
                    beneficiaryId: beneficiarPersonalInfo.id,
                    customFieldTypeId: field.id,
                    value: "",
                };
                newCustomFieldsData.push(compiledData);
            }
        });

        return newCustomFieldsData;
    }, [beneficiarPersonalInfo, editingProfile]);

    useEffect(() => {
        dispatch(getCompagnies()).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled" && resp.payload?.data?.companies?.length) {
                if (page !== "profile" && !companyId && resp.payload.data?.companies.length > 0) {
                    navigate(`/settings/account/${resp.payload.data.companies[0].id}`);
                }
            } else {
                navigate("/management");
            }
        });
    }, [dispatch]);

    useEffect(() => {
        if (companyId) {
            if (!["documents", "profile"].includes(page)) {
                dispatch(fetchBeneficiarPersonalInfo({ companyId: companyId })).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                        setEditingProfile(formatPayload(res.payload, company));
                    }
                });
            } else if (page === "documents") {
                dispatch(fetchDocumentsBP({ companyId: companyId.toString() }));
                setEditingProfile(formatPayload(beneficiarPersonalInfo, company));
            }
            setIsEditing(false);
        }
    }, [companyId, page]);

    useEffect(() => {
        setIsEditing(false);
    }, [companyId]);

    useEffect(() => {
        if (!isMobile) {
            blocker.state === "blocked" && setBlockModal(true);
        } else if (isMobile && isEditing) blocker.state === "blocked";
        else blocker.reset?.();
    }, [blocker, isEditing]);

    /**
     * Formats customFields array into object to be able to save it into Benef data.
     * @param customFields Array that needs to be saved within customFields property.
     * @returns Formatted object.
     */
    const formatCustomFields = (customFields: StoreCustomField[] | undefined) => {
        const reqObj = {};
        customFields &&
            customFields.forEach((field) => {
                if (isObject(field.value)) {
                    // If there has been no change made to a file, I need to stringify it to be able to save:
                    return JSON.stringify(field.value);
                } else {
                    const fullFieldData = companyBenefCustomFields?.find((item) => item.id === field.customFieldTypeId);
                    if (fullFieldData) {
                        reqObj[fullFieldData.uuid] = field.value;
                    }
                }
            });

        return reqObj;
    };

    const handleSubmit = () => {
        if (hasFieldErrors) {
            setRecalculateErrors(true);
            return;
        }

        const payment = editingProfile?.payment;
        if (countryFeatures.bankingFields.includes("iban") && payment?.acceptedPayments.includes("rib")) {
            const ibanVal = payment.iban?.replace(/ /g, "");
            const bicVal = payment.swift?.replace(/ /g, "");
            const errors: { iban?: string; swift?: string } = {};
            if (ibanVal && !isValidIBAN(ibanVal)) errors.iban = t("CompanyBankModal.IbanError");
            if (bicVal && !isValidBIC(bicVal)) errors.swift = t("CompanyBankModal.SwiftError");
            if (Object.keys(errors).length > 0) {
                setIbanBicErrors(errors);
                return;
            }
            setIbanBicErrors({});
        }
        const rawMeta = payment?.bankingMetadata;
        const filteredMeta = rawMeta ? Object.fromEntries(Object.entries(rawMeta).filter(([, v]) => !!v)) : {};
        const bankingMetadata = Object.keys(filteredMeta).length > 0 ? filteredMeta : null;
        dispatch(
            fetchUpdateBeneficaryFromPortal({
                data: {
                    ...editingProfile?.account,
                    ...editingProfile?.tax,
                    ...payment,
                    bankingMetadata,
                    customFields: formatCustomFields(editingProfile?.customFields),
                },
                companyId: +companyId!,
            }),
        ).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                if (isMobile) dispatch(toggleSettingsTab(false));
                setIsEditing(false);
                blocker.reset?.();
            }
        });
    };

    /**
     * Modifies editingProfile whenever there is a change that is made to reflect it visually and value is ready in case changes are saved.
     * @param ev Data of event triggered.
     * @param data Data of the specific page (so editingProfile.account for instance).
     * @param customFieldsInfo Data needed to update and format CF: data = fullCustomFieldsData, field = field saved in Benef info, fullFieldData = data of CF as saved in store.
     */
    const handleChange = (
        ev: any,
        data: any,
        customFieldsInfo?: {
            data: StoreCustomField[];
            field: StoreCustomField | undefined;
            fullFieldData?: ICustomField;
        },
    ) => {
        setEditingProfile((prev) => {
            if (ev.target.name === "acceptedPayments") {
                if ((data[ev.target.name] as string).includes(ev.target.value)) {
                    data[ev.target.name] = data[ev.target.name].replace(ev.target.value, "").trim();
                    if (ev.target.value === "paypal") {
                        data.paypalEmail = "";
                    } else if (ev.target.value === "rib") {
                        data.ribOwnerName = "";
                        data.iban = "";
                        data.swift = "";
                        data.bankingMetadata = {};
                    } else if (ev.target.value === "check") {
                        data.bankCheck = 0;
                    }
                } else {
                    data["acceptedPayments"] = `${data["acceptedPayments"]} ${ev.target.value}`;
                    ev.target.value === "check" && (data.bankCheck = 1);
                }
            } else if (!ev.target.name.includes("customFields")) {
                data[ev.target.name] = ev.target.value;
            }

            const newCustomFieldData =
                customFieldsInfo &&
                formatFieldDataForPortal(
                    customFieldsInfo.data,
                    customFieldsInfo.field,
                    customFieldsInfo.fullFieldData,
                    ev,
                );
            return { ...prev!, [page]: data, customFields: newCustomFieldData ?? prev?.customFields };
        });
        setIsEditing(true);
    };

    /**
     * Formats and saves fileData into customFields property once uploaded.
     * @param data Data in editingProfile.customFields
     * @param fileData Data from the file
     * @param field Field being updated (data from beneficiary store)
     * @param fullFieldData Full data of field (data from customFields store)
     */
    const handleFileSaving = (data: any, fileData: string, field: any, fullFieldData: ICustomField) => {
        const newCustomFieldData = formatFieldDataForPortal(data, field, fullFieldData, fileData);
        setEditingProfile((prev) => {
            return { ...prev!, customFields: newCustomFieldData };
        });
        setIsEditing(true);
    };

    /**
     * Saves change to social profile type.
     * @param socialType Profile type chosen.
     * @param data Data within tax.
     */
    const handleSocialTypeChange = (socialType: { type: string; description: string }, data: any) => {
        data.socialProfileType = socialType.type;
        setEditingProfile((prev: any) => {
            return { ...prev, tax: data };
        });
        setIsEditing(true);
    };

    const handleUploadImage = (ev: ChangeEvent<HTMLInputElement>) => {
        const sizeError = ev.target.files && fileWeightValidator(ev.target.files[0]);
        if (ev.target.files && ev.target.files.length && !sizeError) {
            dispatch(sendPicture(ev.target.files[0])).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setEditingProfile((prev) => {
                        if (prev) {
                            prev.account.avatarUrl = resp.payload as string;
                        }
                        return { ...prev! };
                    });
                    setIsEditing(true);
                }
            });
        } else {
            toast.error(`${t("Settings.FileError", { size: "5" })}`);
        }
    };

    const onExitBlockerAndTab = () => {
        if (isMobile) {
            setBlockModal(false);
            dispatch(toggleSettingsTab(false));
            displayMobileNavBtn(true);
            setIsEditing(false);
        } else {
            blocker.proceed?.();
        }
        // Reset precedent forms errors when switching between tabs forms (account, tax, payment)
        setHasFieldErrors(false);
        setFieldErrors([]);
    };

    const onCloseBlocker = () => {
        if (isMobile) displayMobileNavBtn(true);
        setBlockModal(false);
    };

    const drawerStyle = {
        desktop: {
            ".MuiPaper-root": {
                width: "calc(100vw - 500px)",
                borderLeft: "none",
            },
        },
        mobile: {
            ".MuiPaper-root": {
                width: "100vw",
            },
            ".MuiTabPanel-root": {
                padding: "0",
                paddingBottom: isPWAMode ? "6.5rem" : "5rem",
            },
        },
    };

    const handleScrollToErrors = () => {
        if (!fieldErrors.length) return;
        let errorFieldName = fieldErrors[0];
        if (!isNaN(+errorFieldName)) {
            // handle custom fields errors
            errorFieldName = `customFields.${errorFieldName}`;
        }
        const fieldEl = document.querySelector(`[name="${errorFieldName}"]`);
        if (fieldEl) {
            fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    return (
        <div className={`bp-settings-page${(beneficiarPersonalInfo as any).isDeleted ? " is-deleted" : ""}`}>
            {blocker.state === "blocked" || isMobile ? (
                <ModalBox
                    onClose={() => onCloseBlocker()}
                    open={blockModal}
                    mainTitle={t("BPSettings.ModalBlockTitle")}
                >
                    <div className="block-modal-body">
                        <span>{t("BPSettings.ModalBlockText1")}</span>
                        <span>{t("BPSettings.ModalBlockText2")}</span>
                        <div className="block-modal-actions">
                            <StyledButton onClick={() => onExitBlockerAndTab()} buttonType="secondary">
                                {t("BPSettings.Leave")}
                            </StyledButton>
                            <StyledButton onClick={() => onCloseBlocker()} buttonType="primary">
                                {isMobile ? t("BPSettings.Continue") : t("BPSettings.StayOnProfile")}
                            </StyledButton>
                        </div>
                    </div>
                </ModalBox>
            ) : (
                ""
            )}
            <BPNavigationBarSettings
                page={page}
                companyBenefCustomFields={companyBenefCustomFields}
                customFieldsData={editingProfile?.customFields}
                isInvited={isInvited}
                usesAgreementsOnly={usesAgreementsOnly}
            />
            <Drawer
                open={isMobile ? onOpenSettingsTab : true}
                variant={isMobile ? "temporary" : "permanent"}
                sx={isMobile ? drawerStyle.mobile : drawerStyle.desktop}
                anchor="right"
            >
                <div
                    className={`bp-settings-container${(beneficiarPersonalInfo as any).isDeleted ? " is-deleted" : ""}`}
                >
                    {!!(beneficiarPersonalInfo as any).isDeleted && page !== "documents" && (
                        <div className="deleted-mask">{t("BPSettings.DeletedFromCompany")}</div>
                    )}
                    <TabContext value={page ?? ""}>
                        <TabPanel value="profile">
                            <BPPanelHeader panelTitle={t("NavigationBar.Settings")} />
                            <BPProfile />
                        </TabPanel>
                        {page !== "profile" &&
                            (editingProfile ? (
                                <>
                                    <TabPanel value="account">
                                        <BPPanelHeader
                                            panelTitle={t("Settings.Account")}
                                            isEditing={isEditing}
                                            openBlockModal={() => setBlockModal(true)}
                                            submit={() => handleSubmit()}
                                        />
                                        <BPAccount
                                            entityType={beneficiarPersonalInfo.entity ?? "physical"}
                                            data={editingProfile?.account}
                                            allowChanges={allowChanges}
                                            handleChange={handleChange}
                                            handleUpload={handleUploadImage}
                                            companyBenefCustomFields={companyBenefCustomFields}
                                            customFieldsData={fullCustomFieldsData}
                                            handleFileSaving={handleFileSaving}
                                            isInvited={isInvited}
                                            setHasFieldErrors={setHasFieldErrors}
                                            recalculateErrors={recalculateErrors}
                                            setRecalculateErrors={setRecalculateErrors}
                                            setFieldErrors={setFieldErrors}
                                            fieldErrors={fieldErrors}
                                            countryFeatures={countryFeatures}
                                        />
                                    </TabPanel>
                                    {hasTaxFeatures && (
                                        <TabPanel value="tax">
                                            <BPPanelHeader
                                                panelTitle={t("Settings.TaxProfile")}
                                                isEditing={isEditing}
                                                openBlockModal={() => setBlockModal(true)}
                                                submit={() => handleSubmit()}
                                            />
                                            <BPTaxProfile
                                                data={editingProfile?.tax}
                                                handleChange={handleChange}
                                                countryFeatures={countryFeatures}
                                                allowChanges={allowChanges}
                                                handleSocialTypeChange={handleSocialTypeChange}
                                                companyBenefCustomFields={companyBenefCustomFields}
                                                customFieldsData={editingProfile?.customFields}
                                                handleFileSaving={handleFileSaving}
                                                isInvited={isInvited}
                                                companyId={companyId}
                                            />
                                        </TabPanel>
                                    )}
                                    <TabPanel value="payment">
                                        <BPPanelHeader
                                            panelTitle={t("NewBeneficiary.PaymentsMethods")}
                                            isEditing={isEditing}
                                            openBlockModal={() => setBlockModal(true)}
                                            submit={() => handleSubmit()}
                                        />
                                        <BPPaymentMethod
                                            data={editingProfile?.payment}
                                            handleChange={handleChange}
                                            company={beneficiarCompagnies.find((c) => c.id === +companyId!)}
                                            countryFeatures={countryFeatures}
                                            ibanBicErrors={ibanBicErrors}
                                        />
                                    </TabPanel>
                                </>
                            ) : (
                                <EmptyState type="company" sx={{ margin: "80px auto" }} />
                            ))}
                    </TabContext>
                    {isEditing && !isMobile && (
                        <div className="bp-settings-submit">
                            <StyledButton
                                buttonType="primary"
                                fullWidth
                                onClick={() => {
                                    handleScrollToErrors();
                                    handleSubmit();
                                }}
                            >
                                <CheckOutlined sx={{ width: "18px", marginRight: "10px" }} />
                                {t("Settings.SaveChanges")}
                            </StyledButton>
                        </div>
                    )}
                </div>
            </Drawer>
        </div>
    );
};

export default BPSettings;
